
import React, { useCallback, useEffect, useRef, useState, useMemo, Suspense, lazy } from 'react';
import { Coordinates, GpxRoute, AppUser, SvvCamera } from '../../types';
import { Share2, Edit3, Map as MapIcon, ArrowLeft, Activity, MapPin, Clock, Tent, Coffee, Mountain, ExternalLink, Image as ImageIcon, Landmark, RefreshCcw, Loader2, User, Heart, Send, Bird, Navigation, Download, FileDown, Lock, FolderPlus, Eye, Camera, Orbit, AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ElevationProfile from './ElevationProfile';
import ReviewSystem from '../ReviewSystem';
import { recommendRouteForPublishing, fetchGpxWithProxy, fetchSpotsByIds, translateRouteText, logFeatureUsage, db } from '../../services/firebaseService';
import { doc, updateDoc } from 'firebase/firestore';
import { isAdminUser } from '../../services/accessControl';
import { usePremiumNudge } from '../../contexts/PremiumNudgeContext';
import VehicleBadge from '../common/VehicleBadge';
import { APP_URL, TERRAIN_META } from '../../constants';
import { fetchCamerasNearRoute, fetchCamerasNearSpot } from '../../services/svvCameraService';
import { useRouteSeasonalWarnings } from '../../hooks/useRouteSeasonalWarnings';
import { useToast } from '../../contexts/ToastContext';
import { getDistance } from '../../services/geometryUtils';
import { emitRewardEvent } from '../../services/rewardEvents';

import { SubscriptionModal } from '../SubscriptionModal';
const Route3DFlyover = lazy(() => import('./Route3DFlyover'));

interface RouteDetailViewProps {
   route: GpxRoute;
   onClose: () => void;
   onEdit: () => void;
   onShowMap?: () => void;
   onReanalyze?: (route: GpxRoute) => void;
   onReanalyzeLayer?: (route: GpxRoute, layer: string) => void;
   user: AppUser | null;
   isBackgroundAnalyzing?: boolean;
   isFavorite?: boolean;
   onToggleFavorite?: () => void;
   onOpenAuth?: () => void;
   onRouteStatusChange?: (routeId: string, newStatus: string) => void;
   isLoading?: boolean;
   isPaidUser?: boolean;
   onOpenUpgrade?: () => void;
   onAddRouteToMap?: (routeId: string) => void;
   hasCollaborativeMaps?: boolean;
   routeFacilityLoading?: {
      routeId: string | null;
      fricamp: boolean;
      restAreas: boolean;
      campsites: boolean;
      attractions: boolean;
      chargers: boolean;
      wildlife: boolean;
   };
   onStartFollowRoute?: (route: GpxRoute) => void;
   onNavigateToRoute?: (route: GpxRoute) => void;
   trackingStatus?: 'idle' | 'recording' | 'paused';
   isDraftRoute?: boolean;
   routePath?: Coordinates[] | null;
   onRouteWebcamsChange?: (cameras: SvvCamera[]) => void;
}

const RouteDetailView: React.FC<RouteDetailViewProps> = ({
   route, onClose, onEdit, onShowMap, onReanalyze, onReanalyzeLayer, user, isBackgroundAnalyzing, isFavorite, onToggleFavorite, onOpenAuth, onRouteStatusChange, isLoading, isPaidUser = false, onOpenUpgrade, onAddRouteToMap, hasCollaborativeMaps = false, routeFacilityLoading, onStartFollowRoute, onNavigateToRoute, trackingStatus = 'idle', isDraftRoute = false, routePath = null, onRouteWebcamsChange
}) => {
   const { t, i18n } = useTranslation();
   const toast = useToast();
   const showNudge = usePremiumNudge();
   const [isRecommending, setIsRecommending] = useState(false);
   const [confirmRecommend, setConfirmRecommend] = useState(false);
   const [showShareModal, setShowShareModal] = useState(false);
   const [isLoadingGpx, setIsLoadingGpx] = useState(false);
   const [includeFricamp, setIncludeFricamp] = useState(true);
   const [includeAttractions, setIncludeAttractions] = useState(false);
   const [translatedName, setTranslatedName] = useState<string | null>(null);
   const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
   const [isTranslating, setIsTranslating] = useState(false);
   const requestedTranslationsRef = useRef<Set<string>>(new Set());
   const activeRouteLoading = routeFacilityLoading?.routeId === route.id ? routeFacilityLoading : null;
   const currentLanguage = i18n.language || 'no';
   const [routeWebcams, setRouteWebcams] = useState<(SvvCamera & { _distance: number; _routeOrderM?: number })[]>([]);
   const [showWebcams, setShowWebcams] = useState(false);
   const [fullscreenWebcam, setFullscreenWebcam] = useState<SvvCamera | null>(null);
   const [failedWebcamIds, setFailedWebcamIds] = useState<Set<string>>(new Set());
   const [showFlyover, setShowFlyover] = useState(false);
   const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

   const isOffroadRoute = route.terrainTypes?.includes('offroad') || route.isPremiumGated;
   const canAccessOffroad = isPaidUser || isAdminUser(user);
   const isLocked = isOffroadRoute && !canAccessOffroad;

   const { warnings: seasonalWarnings } = useRouteSeasonalWarnings(route);

   const getRouteOrderMeters = React.useCallback((camera: SvvCamera, path: Coordinates[]): number => {
      let total = 0;
      let bestDistance = Number.POSITIVE_INFINITY;
      let bestOrder = Number.POSITIVE_INFINITY;
      for (let i = 1; i < path.length; i++) {
         const prev = path[i - 1];
         const curr = path[i];
         const segmentLength = getDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
         const distToCurr = getDistance(camera.latitude, camera.longitude, curr.latitude, curr.longitude);
         if (distToCurr < bestDistance) {
            bestDistance = distToCurr;
            bestOrder = total + segmentLength;
         }
         total += segmentLength;
      }
      return Number.isFinite(bestOrder) ? bestOrder : total;
   }, []);

   const getCameraRoutePath = React.useCallback(async (): Promise<Coordinates[] | null> => {
      if (routePath && routePath.length > 1) return routePath;
      if (!route.gpxUrl) return null;

      const gpxText = await fetchGpxWithProxy(route.gpxUrl);
      const parser = new DOMParser();
      const xml = parser.parseFromString(gpxText, 'application/xml');
      const trkpts = Array.from(xml.querySelectorAll('trkpt'));
      const parsedPath = trkpts
         .map((pt) => ({
            latitude: parseFloat(pt.getAttribute('lat') || 'NaN'),
            longitude: parseFloat(pt.getAttribute('lon') || 'NaN'),
         }))
         .filter((coords) => Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude));

      return parsedPath.length > 1 ? parsedPath : null;
   }, [route.gpxUrl, routePath]);

   useEffect(() => {
      let cancelled = false;
      getCameraRoutePath()
         .then((cameraPath) => {
            if (cancelled) return;
            if (cameraPath && cameraPath.length > 1) {
               return fetchCamerasNearRoute(cameraPath, 2000)
                  .then((cams) => {
                     if (cancelled) return;
                     const ordered = cams
                        .map((cam) => ({
                           ...cam,
                           _distance: Math.round(getDistance(cameraPath[0].latitude, cameraPath[0].longitude, cam.latitude, cam.longitude)),
                           _routeOrderM: getRouteOrderMeters(cam, cameraPath),
                        }))
                        .sort((a, b) => (a._routeOrderM ?? Number.POSITIVE_INFINITY) - (b._routeOrderM ?? Number.POSITIVE_INFINITY));
                     setRouteWebcams(ordered);
                  })
                  .catch(() => {
                     if (!cancelled) setRouteWebcams([]);
                  });
            }

            if (!route.center) {
               setRouteWebcams([]);
               return;
            }

            const searchRadius = Math.max(5000, (route.distance || 10) * 500);
            return fetchCamerasNearSpot(route.center, Math.min(searchRadius, 50000))
               .then((cams) => {
                  if (!cancelled) setRouteWebcams(cams);
               })
               .catch(() => {
                  if (!cancelled) setRouteWebcams([]);
               });
         })
         .catch(() => {
            if (!cancelled) setRouteWebcams([]);
         });
      return () => { cancelled = true; };
   }, [getCameraRoutePath, getRouteOrderMeters, route.center, route.distance, route.id]);

   const visibleWebcams = useMemo(() =>
      routeWebcams.filter(c => !failedWebcamIds.has(c.id)),
   [routeWebcams, failedWebcamIds]);

   useEffect(() => {
      if (!onRouteWebcamsChange) return;
      onRouteWebcamsChange(showWebcams ? visibleWebcams : []);
      return () => onRouteWebcamsChange([]);
   }, [onRouteWebcamsChange, showWebcams, visibleWebcams]);
   const [contextMenu, setContextMenu] = useState<{ layer: string; label: string; x: number; y: number } | null>(null);
   const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const handleContextMenu = (e: React.MouseEvent, layer: string, label: string) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ layer, label, x: e.clientX, y: e.clientY });
   };

   const handleTouchStart = (layer: string, label: string) => (e: React.TouchEvent) => {
      const touch = e.touches[0];
      longPressTimerRef.current = setTimeout(() => {
         if (navigator.vibrate) navigator.vibrate(30);
         setContextMenu({ layer, label, x: touch.clientX, y: touch.clientY });
      }, 600);
   };

   const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
         clearTimeout(longPressTimerRef.current);
         longPressTimerRef.current = null;
      }
   };

   useEffect(() => {
      if (!contextMenu) return;
      const dismiss = () => setContextMenu(null);
      document.addEventListener('click', dismiss);
      document.addEventListener('scroll', dismiss, true);
      return () => {
         document.removeEventListener('click', dismiss);
         document.removeEventListener('scroll', dismiss, true);
      };
   }, [contextMenu]);

   useEffect(() => {
      const isNorwegian = currentLanguage.startsWith('no');
      if (isNorwegian) {
         setTranslatedName(null);
         setTranslatedDescription(null);
         return;
      }

      setTranslatedName(null);
      setTranslatedDescription(null);

      const lang = currentLanguage.startsWith('de') ? 'de' : 'en';
      const cached = route.translations?.[lang];
      if (cached?.name && cached?.description) {
         setTranslatedName(cached.name);
         setTranslatedDescription(cached.description);
         return;
      }

      const requestKey = `${route.id}:${lang}`;
      if (requestedTranslationsRef.current.has(requestKey)) {
         return;
      }
      requestedTranslationsRef.current.add(requestKey);

      let cancelled = false;
      setIsTranslating(true);
      translateRouteText(route.id, lang)
         .then((res) => {
            if (cancelled) return;
            if (res.ok && res.data) {
               setTranslatedName(res.data.name);
               setTranslatedDescription(res.data.description);
            }
         })
         .catch(() => {
            // Keep original route text when translation fails.
            requestedTranslationsRef.current.delete(requestKey);
         })
         .finally(() => { if (!cancelled) setIsTranslating(false); });

      return () => { cancelled = true; };
   }, [route.id, route.translations, currentLanguage]);

   const isAnonymousCreatorLabel = (name?: string | null) => {
      const normalized = String(name || '').trim().toLowerCase();
      return !normalized || normalized === 'anonym' || normalized === 'anonymous';
   };

   const displayRouteName =
      translatedName ||
      route.name ||
      [route.startPlace, route.endPlace].filter(Boolean).join(' → ') ||
      route.slug?.replace(/-/g, ' ') ||
      t('route.trip', 'Tur');
   const displayRouteDescription = translatedDescription || route.description || '';
   const displayCreatorName =
      (route.createdBy && user?.uid === route.createdBy ? (user.displayName || user.email || t('profile.tabs.profile', 'Profil')) : null) ||
      (!isAnonymousCreatorLabel(route.createdByName) ? route.createdByName : null) ||
      t('route.anonymous');
   const displayCreatorPhoto =
      route.createdByPhoto ||
      (route.createdBy && user?.uid === route.createdBy ? user.photoURL || undefined : undefined);

   const escapeXml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

   const enrichGpxWithPois = async (gpxText: string): Promise<string> => {
      const waypoints: string[] = [];

      if (includeFricamp && route.associatedSpotIds?.length) {
         const spots = await fetchSpotsByIds(route.associatedSpotIds);
         spots.forEach(s => {
            waypoints.push(`<wpt lat="${s.location.latitude}" lon="${s.location.longitude}"><name>${escapeXml(s.name)}</name><type>Fricamp</type></wpt>`);
         });
      }

      if (includeAttractions && route.wikiSpots?.length) {
         route.wikiSpots.forEach(w => {
            waypoints.push(`<wpt lat="${w.location.latitude}" lon="${w.location.longitude}"><name>${escapeXml(w.title)}</name><type>Attraction</type></wpt>`);
         });
      }

      if (waypoints.length === 0) return gpxText;

      // Insert waypoints before the <trk> element
      return gpxText.replace(/<trk>/, waypoints.join('\n') + '\n<trk>');
   };

   const getGpxCoordinates = async (): Promise<{ lat: number; lon: number }[]> => {
      const gpxText = await fetchGpxWithProxy(route.gpxUrl);
      const parser = new DOMParser();
      const xml = parser.parseFromString(gpxText, 'application/xml');
      const trkpts = Array.from(xml.querySelectorAll('trkpt'));
      return trkpts.map(pt => ({
         lat: parseFloat(pt.getAttribute('lat') || '0'),
         lon: parseFloat(pt.getAttribute('lon') || '0')
      }));
   };

   const sampleWaypoints = (coords: { lat: number; lon: number }[], maxPoints: number = 23) => {
      if (coords.length <= maxPoints) return coords;
      const step = (coords.length - 1) / (maxPoints - 1);
      return Array.from({ length: maxPoints }, (_, i) => coords[Math.round(i * step)]);
   };

   const handleOpenGoogleMaps = async () => {
      setIsLoadingGpx(true);
      try {
         const coords = await getGpxCoordinates();
         if (coords.length < 2) return;
         const sampled = sampleWaypoints(coords);
         const origin = sampled[0];
         const destination = sampled[sampled.length - 1];
         const waypoints = sampled.slice(1, -1).map(c => `${c.lat},${c.lon}`).join('|');
         const url = `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lon}&destination=${destination.lat},${destination.lon}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
         window.open(url, '_blank');
      } catch (err) {
         console.error('Google Maps open failed:', err);
      } finally {
         setIsLoadingGpx(false);
      }
   };

   const handleShareGpx = async () => {
      setIsLoadingGpx(true);
      try {
         const rawGpx = await fetchGpxWithProxy(route.gpxUrl);
         const gpxText = await enrichGpxWithPois(rawGpx);
         const fileName = `${displayRouteName.replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_')}.gpx`;
         const file = new File([gpxText], fileName, { type: 'application/gpx+xml' });
         if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: displayRouteName });
         } else {
            downloadGpxBlob(gpxText, fileName);
         }
      } catch (err) {
         if ((err as Error).name !== 'AbortError') {
            console.error('GPX share failed:', err);
         }
      } finally {
         setIsLoadingGpx(false);
      }
   };

   const handleDownloadGpx = async () => {
      setIsLoadingGpx(true);
      try {
         const rawGpx = await fetchGpxWithProxy(route.gpxUrl);
         const gpxText = await enrichGpxWithPois(rawGpx);
         const fileName = `${displayRouteName.replace(/[^a-zA-Z0-9æøåÆØÅ_-]/g, '_')}.gpx`;
         downloadGpxBlob(gpxText, fileName);
      } catch (err) {
         console.error('GPX download failed:', err);
      } finally {
         setIsLoadingGpx(false);
      }
   };

   const downloadGpxBlob = (gpxText: string, fileName: string) => {
      const blob = new Blob([gpxText], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
   };

   const performShare = async (shareUrl: string, title?: string) => {
      if (navigator.share) {
         try {
            await navigator.share({
               title: title || displayRouteName,
               text: t('route.sharing_route', { name: displayRouteName }),
               url: shareUrl
            });
            logFeatureUsage('share');
            emitRewardEvent({
               kind: 'share_route',
               title: displayRouteName,
               message: 'Du sprer ruten og gjør det enklere for andre å oppdage den.',
               rewardPoints: 10,
            });
         } catch (err) {
            if ((err as Error).name !== 'AbortError') {
               console.error("Deling feilet:", err);
            }
         }
      } else {
         navigator.clipboard.writeText(shareUrl);
         toast(t('route.link_copied'), 'success');
         emitRewardEvent({
            kind: 'share_route',
            title: displayRouteName,
            message: 'Lenken er kopiert, og delingen teller som et lite bidrag.',
            rewardPoints: 10,
         });
      }
      setShowShareModal(false);
   };

   const getShareUrl = async (base: string) => {
      if (route.status === 'private') {
        let token = route.shareToken;
        if (!token) {
          token = Math.random().toString(16).slice(2, 10);
          try { await updateDoc(doc(db, 'routes', route.id), { shareToken: token }); } catch {}
          route.shareToken = token;
        }
        return `${base}?token=${token}`;
      }
      return base;
   };

   const handleShare = () => {
      setShowShareModal(true);
   };

   const handleRecommend = async () => {
      if (!user) return;
      if (!confirmRecommend) { setConfirmRecommend(true); return; }
      setConfirmRecommend(false);
      setIsRecommending(true);
      try {
         await recommendRouteForPublishing(route, user);
         onRouteStatusChange?.(route.id, 'pending');
         toast(t('route.recommend_success'), 'success');
         emitRewardEvent({
            kind: 'publish_route',
            title: displayRouteName,
            message: 'Ruten er sendt inn til publisering og kan hjelpe flere på tur.',
            rewardPoints: 30,
         });
      } catch (err) {
         console.error('Recommend failed:', err);
      } finally {
         setIsRecommending(false);
      }
   };

   const isAdmin = isAdminUser(user);
   const canEdit = !isDraftRoute && user && (route.createdBy === user.uid || isAdmin);

   const SUIT_CAT: Record<string, { emoji: string; label: string }> = {
      motorhome: { emoji: '🚐', label: 'Bobil' }, campervan: { emoji: '🚌', label: 'Campervan' },
      caravan: { emoji: '🏠', label: 'Campingvogn' }, camper_trailer: { emoji: '🪝', label: 'Campinghenger' },
      rooftent: { emoji: '⛺', label: 'Taktelt' }, offroad: { emoji: '🚙', label: 'Offroad/4x4' },
   };
   // Terrain metadata from shared TERRAIN_META in constants.ts

   return (
      <div className="flex flex-col h-full bg-[var(--bg-surface-alt)] text-[var(--text-primary)] overflow-y-auto custom-scrollbar animate-in slide-in-from-right-4 duration-300 pb-0">
         <div className="px-3 py-2.5 border-b border-[var(--border-primary)] flex items-center justify-between sticky top-0 bg-[var(--bg-surface-alt)]/90 backdrop-blur-xl z-20">
            <div className="flex items-center gap-2 shrink-0">
               <button onClick={onClose} className="p-1.5 bg-[var(--bg-input)] hover:bg-[var(--bg-input)] rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors border border-[var(--border-primary)]">
                  <ArrowLeft size={16} />
               </button>
               <span className="text-2xs font-black uppercase tracking-widest text-emerald-500">
                  {isDraftRoute ? 'Planlagt rute' : (isBackgroundAnalyzing ? t('route.analyzing') : t('route.details'))}
               </span>
            </div>
            <div className="flex items-center gap-0 sm:gap-1.5 flex-shrink min-w-0 overflow-x-auto no-scrollbar">
               {!isDraftRoute && onToggleFavorite && (
                  <button
                     onClick={() => { 
                        if (isLocked) setShowSubscriptionModal(true);
                        else if (!user && onOpenAuth) onOpenAuth(); 
                        else onToggleFavorite(); 
                     }}
                     className={`p-1.5 sm:p-2.5 transition-all rounded-xl hover:bg-[var(--bg-surface)] ${isFavorite ? 'text-red-500' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'} ${isLocked ? 'opacity-50' : ''}`}
                     title={isFavorite ? t('route.remove_favorite') : t('route.add_favorite')}
                  >
                     {isLocked ? <Lock size={16} /> : <Heart size={16} className={`sm:w-[18px] sm:h-[18px] ${isFavorite ? "scale-110" : ""}`} fill={isFavorite ? "currentColor" : "none"} />}
                  </button>
               )}

               {/* Re-analyze Button - Only for logged in users */}
               {!isDraftRoute && user && onReanalyze && (
                  <button
                     onClick={() => onReanalyze(route)}
                     disabled={isBackgroundAnalyzing}
                     className={`p-1.5 sm:p-2.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-xl hover:bg-[var(--bg-surface)] ${isBackgroundAnalyzing ? 'bg-emerald-500/10 text-emerald-500' : ''}`}
                     title={isBackgroundAnalyzing ? t('route.analyzing') : t('route.reanalyze')}
                  >
                     {isBackgroundAnalyzing ? <Loader2 size={16} className="animate-spin sm:w-[18px] sm:h-[18px]" /> : <RefreshCcw size={16} className="sm:w-[18px] sm:h-[18px]" />}
                  </button>
               )}

               {!isDraftRoute && user && onAddRouteToMap && (
                  <button
                     onClick={() => onAddRouteToMap(route.id)}
                     className="p-1.5 sm:p-2.5 text-[var(--text-secondary)] hover:text-sky-400 transition-colors rounded-xl hover:bg-[var(--bg-surface)]"
                     title={t('route.add_to_map')}
                  >
                     <FolderPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
               )}

               {!isDraftRoute && (
                  <button
                     onClick={() => isLocked ? setShowSubscriptionModal(true) : handleShare()}
                     className={`p-1.5 sm:p-2.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-xl hover:bg-[var(--bg-surface)] ${isLocked ? 'opacity-50' : ''}`}
                     title={t('route.share')}
                  >
                     {isLocked ? <Lock size={16} /> : <Share2 size={16} className="sm:w-[18px] sm:h-[18px]" />}
                  </button>
               )}

               {canEdit && (
                  <button onClick={onEdit} className="p-1.5 sm:p-2.5 text-[var(--text-secondary)] hover:text-emerald-400 transition-colors rounded-xl hover:bg-[var(--bg-surface)]" title={t('route.edit')}>
                     <Edit3 size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
               )}

            </div>
         </div>

         {/* Navigation action buttons — separate row for visibility */}
         {trackingStatus === 'idle' && (onShowMap || onStartFollowRoute || onNavigateToRoute) && (
            <div className="px-3 py-2 border-b border-[var(--border-primary)] flex items-center gap-2 bg-[var(--bg-surface-alt)]/90 backdrop-blur-xl sticky top-[49px] z-20">
               {onShowMap && (
                  <button
                     onClick={() => isLocked ? setShowSubscriptionModal(true) : onShowMap()}
                     className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 ${isLocked ? 'bg-amber-600/15 text-amber-400 border-amber-500/30' : 'bg-emerald-600/15 text-emerald-400 border-emerald-500/30'} hover:opacity-80 rounded-xl transition-colors`}
                     title={t('route.show_on_map')}
                  >
                     {isLocked ? <Lock size={14} /> : <MapIcon size={14} />} <span className="text-2xs font-black uppercase">{t("auto.routes.routedetailview.vis_i_kart", "Vis i kart")}</span>
                  </button>
               )}
               {onStartFollowRoute && (
                  <button
                     onClick={() => isLocked ? setShowSubscriptionModal(true) : onStartFollowRoute(route)}
                     className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 ${isLocked ? 'bg-amber-600/15 text-amber-400 border-amber-500/30' : 'bg-blue-600/15 text-blue-400 border-blue-500/30'} hover:opacity-80 rounded-xl transition-colors`}
                     title={t("auto.routes.routedetailview.title.kjor_hele_ruten_fra_start", "Kjør hele ruten fra start")}
                  >
                     {isLocked ? <Lock size={14} /> : <Navigation size={14} />} <span className="text-2xs font-black uppercase">{t("auto.routes.routedetailview.kjor_ruten", "Kjør ruten")}</span>
                  </button>
               )}
               {onNavigateToRoute && (
                  <button
                     onClick={() => isLocked ? setShowSubscriptionModal(true) : onNavigateToRoute(route)}
                     className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-600/15 hover:bg-amber-600/25 border border-amber-500/30 rounded-xl text-amber-400 hover:text-amber-300 transition-colors"
                     title={t("auto.routes.routedetailview.title.naviger_fra_din_posisjon_til_n", "Naviger fra din posisjon til nærmeste punkt på ruten")}
                  >
                     {isLocked ? <Lock size={14} /> : <Navigation size={14} />} <span className="text-2xs font-black uppercase">{t("auto.routes.routedetailview.naviger_hit", "Naviger hit")}</span>
                  </button>
               )}
            </div>
         )}

         {/* Reduced padding from p-5 to p-4, reduced space-y-4 to space-y-3 */}
         <div className="p-4 space-y-3">
            <div>
               <span className="px-2 py-0.5 rounded-md bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-tiny font-black uppercase tracking-widest">
                  {route.region ? t(`regions.${route.region}`) : t('route.trip')}
               </span>
               <h1 className="text-xl md:text-2xl font-black text-[var(--text-primary)] tracking-tight mt-2 leading-tight">{displayRouteName}</h1>
               <div className="flex items-center gap-2 mt-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-[var(--border-secondary)] bg-[var(--bg-input)] flex items-center justify-center">
                     {displayCreatorPhoto ? (
                        <img src={displayCreatorPhoto} alt="" className="w-full h-full object-cover" />
                     ) : (
                        <User size={12} className="text-[var(--text-muted)]" />
                     )}
                  </div>
                  <p className="text-tiny font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('route.by')} {displayCreatorName}</p>
                  {route.creatorVehicleCategory && (
                    <VehicleBadge category={route.creatorVehicleCategory} length={route.creatorVehicleLength} is4wd={route.creatorVehicleIs4wd} />
                  )}
               </div>
            </div>

            {/* Stats Bar */}
            {/* Personal vehicle suitability card */}
            {user?.activeVehicleCategory && route.suitableCategories?.length ? (() => {
               const userCat = user.activeVehicleCategory!;
               const userMeta = SUIT_CAT[userCat];
               const catMatch = route.suitableCategories!.includes(userCat) ||
                  (user.activeVehicleEquipment?.four_wheel_drive && route.suitableCategories!.includes('offroad'));
               const tooLong = !!(user.activeVehicleLength && route.maxRecommendedLengthCm && user.activeVehicleLength > route.maxRecommendedLengthCm);
               const needs4wd = !!(route.requires4wd && !user.activeVehicleEquipment?.four_wheel_drive);
               const isGreen = catMatch && !tooLong && !needs4wd;
               const isAmber = catMatch && (tooLong || needs4wd);
               const borderColor = isGreen ? 'border-emerald-500/30' : isAmber ? 'border-amber-500/30' : 'border-red-500/30';
               const bgColor = isGreen ? 'bg-emerald-500/5' : isAmber ? 'bg-amber-500/5' : 'bg-red-500/5';
               const iconColor = isGreen ? 'text-emerald-400' : isAmber ? 'text-amber-400' : 'text-red-400';
               return (
                  <div className={`${bgColor} ${borderColor} border rounded-xl p-3 space-y-2`}>
                     <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                           <span className={`text-sm ${iconColor}`}>{isGreen ? '✓' : isAmber ? '⚠' : '✗'}</span>
                           <span className={`text-tiny font-black uppercase tracking-widest ${iconColor}`}>
                              {isGreen ? t('route.vehicle_match_good', 'Passer ditt kjøretøy') : isAmber ? t('route.vehicle_match_warn', 'Passer med forbehold') : t('route.vehicle_match_bad', 'Ikke anbefalt for deg')}
                           </span>
                        </div>
                        {userMeta && <span className="text-2xs font-bold text-[var(--text-muted)]">{userMeta.emoji} {userMeta.label}</span>}
                     </div>
                     <div className="flex flex-wrap gap-1.5">
                        {/* Category match */}
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-3xs font-bold ${catMatch ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                           {catMatch ? '✓' : '✗'} {t('route.vehicle_category', 'Kjøretøytype')}
                        </span>
                        {/* Length check */}
                        {route.maxRecommendedLengthCm && (
                           <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-3xs font-bold ${tooLong ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                              {tooLong ? '⚠' : '✓'} {t('route.max_length', 'Maks')} {(route.maxRecommendedLengthCm / 100).toFixed(1)}m
                              {user.activeVehicleLength ? ` (${t('route.yours', 'din')}: ${(user.activeVehicleLength / 100).toFixed(1)}m)` : ''}
                           </span>
                        )}
                        {/* 4WD check */}
                        {route.requires4wd && (
                           <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-3xs font-bold ${needs4wd ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                              {needs4wd ? '⚠' : '✓'} 4WD {needs4wd ? t('route.required', 'kreves') : ''}
                           </span>
                        )}
                     </div>
                     {route.suitabilityNote && (
                        <p className="text-2xs text-[var(--text-muted)] italic leading-relaxed">{route.suitabilityNote}</p>
                     )}
                     {/* Other suitable categories */}
                     {(() => {
                        const otherCats = route.suitableCategories!.filter(c => c !== userCat);
                        return otherCats.length > 0 ? (
                           <div className="flex items-center gap-1.5 pt-1 border-t border-[var(--border-primary)]">
                              <span className="text-3xs text-[var(--text-muted)] font-bold">{t('route.also_suitable', 'Også egnet')}:</span>
                              {otherCats.map(cat => {
                                 const m = SUIT_CAT[cat];
                                 return m ? <span key={cat} className="text-3xs px-1.5 py-0.5 rounded bg-[var(--bg-input)] text-[var(--text-muted)] font-bold">{m.emoji} {m.label}</span> : null;
                              })}
                           </div>
                        ) : null;
                     })()}
                  </div>
               );
            })() : route.suitableCategories?.length ? (
               /* No user vehicle — show general suitability badges */
               <div className="flex gap-2 flex-wrap">
                  {route.suitableCategories.map(cat => {
                     const m = SUIT_CAT[cat];
                     return m ? <span key={cat} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-2xs font-bold">{m.emoji} {m.label}</span> : null;
                  })}
                  {route.requires4wd && <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-400 text-2xs font-bold">4WD</span>}
                  {route.maxRecommendedLengthCm && <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] text-2xs font-bold">{t('route.max_length', 'Maks')} {(route.maxRecommendedLengthCm / 100).toFixed(1)}m</span>}
               </div>
            ) : null}
            {/* Stats bar */}
            <div className="flex gap-2 flex-wrap">
               {route.distance && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)]">
                     <MapPin size={12} />
                     <span className="text-tiny font-black uppercase tracking-widest">{route.distance.toFixed(1)} km</span>
                  </div>
               )}
               {route.estimatedTime && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)]">
                     <Clock size={12} />
                     <span className="text-tiny font-black uppercase tracking-widest">{route.estimatedTime}</span>
                  </div>
               )}
               {route.view_count != null && route.view_count > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)]">
                     <Eye size={12} />
                     <span className="text-tiny font-black uppercase tracking-widest">{route.view_count} {t('route.views')}</span>
                  </div>
               )}
            </div>

            {/* Contextual season indicator */}
            {(() => {
               const month = new Date().getMonth();
               const currentSeason: 'spring' | 'summer' | 'autumn' | 'winter' = month >= 2 && month <= 4 ? 'spring' : month >= 5 && month <= 7 ? 'summer' : month >= 8 && month <= 10 ? 'autumn' : 'winter';
               const SEASON_LABELS: Record<string, string> = { spring: 'Vår', summer: 'Sommer', autumn: 'Høst', winter: 'Vinter' };
               const isWinterish = currentSeason === 'winter' || (currentSeason === 'spring' && month <= 3);
               const inSeason = route.bestSeasons?.includes(currentSeason);
               const winterBad = isWinterish && route.winterSuitability === 'not_recommended';

               if (winterBad) return (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                     <span className="text-sm">❄️</span>
                     <span className="text-tiny font-bold text-red-400">Ikke anbefalt nå ({SEASON_LABELS[currentSeason]}) — sommerrute</span>
                  </div>
               );
               if (inSeason) return (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                     <span className="text-sm">🟢</span>
                     <span className="text-tiny font-bold text-emerald-400">God sesong nå ({SEASON_LABELS[currentSeason]})</span>
                  </div>
               );
               if (route.bestSeasons?.length && !inSeason) return (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-primary)]">
                     <span className="text-sm">📅</span>
                     <span className="text-tiny text-[var(--text-muted)]">Best egnet: {route.bestSeasons.map(s => SEASON_LABELS[s] || s).join(', ')}</span>
                  </div>
               );
               return null;
            })()}

            {(route.terrainTypes?.length || route.suitabilityNote || route.winterSuitability) && (
               <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {route.terrainTypes?.map(t => {
                     const m = TERRAIN_META[t];
                     return m ? <span key={t} className="px-2 py-0.5 rounded-md bg-[var(--bg-surface)] border border-[var(--border-primary)] text-3xs text-[var(--text-muted)]">{m.emoji} {m.label}</span> : null;
                  })}
                  {/* winterSuitability badges removed — contextual season indicator above already communicates this */}
                  {route.suitabilityNote && <span className="text-2xs text-[var(--text-muted)] italic">{route.suitabilityNote}</span>}
               </div>
            )}

            {/* Image Gallery */}
            {route.imageUrls && route.imageUrls.length > 0 && (
               <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                  {route.imageUrls.map((url, i) => (
                     <div key={i} className="h-28 md:h-32 w-auto min-w-[120px] rounded-2xl border border-[var(--border-secondary)] shadow-lg overflow-hidden relative bg-[var(--bg-surface-alt)] shrink-0">
                        <img src={url} className="h-full w-full object-cover" alt="" />
                     </div>
                  ))}
               </div>
            )}

            {/* Description */}
            <div className="bg-[var(--bg-surface)] p-4 md:p-5 rounded-3xl border border-[var(--border-primary)]">
               <p className="text-xs md:text-sm text-[var(--text-secondary)] leading-relaxed font-medium">
                  {displayRouteDescription}
               </p>
               {isTranslating && (
                  <p className="text-2xs text-[var(--text-muted)] mt-2 italic animate-pulse">{t('route.translating')}</p>
               )}
               {route.seasonalNote && (
                  <p className="text-2xs text-[var(--text-muted)] mt-2 italic">{route.seasonalNote}</p>
               )}
            </div>

            {/* Seasonal Warnings */}
            {seasonalWarnings.length > 0 && (
               <div className="space-y-2">
                  {seasonalWarnings.map((w, i) => (
                     <div key={i} className={`flex items-start gap-2.5 p-3 rounded-2xl border ${
                        w.severity === 'danger' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                        w.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
                        'bg-blue-500/10 border-blue-500/30 text-blue-400'
                     }`}>
                        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                           <span className="text-tiny font-bold">{w.message}</span>
                           {w.location && <span className="text-2xs opacity-70 ml-1">{w.location}</span>}
                        </div>
                     </div>
                  ))}
                  {visibleWebcams.length > 0 && seasonalWarnings.some(w => w.type === 'snow' || w.type === 'winter_closed') && (
                     <button
                        onClick={() => { setShowWebcams(true); document.getElementById('route-webcams')?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="flex items-center gap-1.5 text-2xs font-bold text-cyan-400 hover:text-cyan-300 transition-colors ml-1"
                     >
                        <Camera size={12} /> {t('route.see_road_cameras', 'Se veibilder langs ruten')} ({visibleWebcams.length})
                     </button>
                  )}
               </div>
            )}

            {/* Facilities Stats - HEAVILY COMPRESSED FOR MOBILE */}
            {(route.stats || isBackgroundAnalyzing) && (
               <div className="space-y-1.5">
                  <div className="flex justify-between items-end ml-1">
                     <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest">{t('route.facilities')}</h3>
                     {isBackgroundAnalyzing && route.analyzeProgress && (
                        <span className="text-tiny font-black text-emerald-500 animate-pulse uppercase tracking-widest">
                           {route.analyzeProgress.current}/{route.analyzeProgress.total}
                        </span>
                     )}
                     {isBackgroundAnalyzing && !route.analyzeProgress && (
                        <span className="text-tiny font-black text-emerald-500 animate-pulse uppercase tracking-widest">{t('route.updating')}</span>
                     )}
                  </div>
                  {/* Progress bar during analysis */}
                  {isBackgroundAnalyzing && route.analyzeProgress && route.analyzeProgress.total > 0 && (
                     <div className="w-full bg-[var(--bg-input)] rounded-full h-1 overflow-hidden">
                        <div
                           className="bg-emerald-500 h-1 rounded-full transition-all duration-500 ease-out"
                           style={{ width: `${(route.analyzeProgress.current / route.analyzeProgress.total) * 100}%` }}
                        />
                     </div>
                  )}
                  <div className="grid grid-cols-6 gap-1.5 pb-1">
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'fricamp', t('route.fricamp'))} onTouchStart={handleTouchStart('fricamp', t('route.fricamp'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-emerald-900/20 hover:bg-emerald-900/30 py-2 px-1 rounded-xl border border-emerald-500/20 hover:border-emerald-500/40 flex flex-col items-center transition-all active:scale-95">
                        <Tent size={12} className="text-emerald-500 mb-0.5" />
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.fricamp && (route.stats?.wildSpotsCount ?? 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.wildSpotsCount ?? 0)}
                        </span>
                        <span className="text-[7px] font-black text-emerald-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.fricamp')}</span>
                        {activeRouteLoading?.fricamp && <Loader2 size={8} className="animate-spin text-emerald-400/80 mt-0.5" />}
                     </button>
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'restAreas', t('route.rest_area'))} onTouchStart={handleTouchStart('restAreas', t('route.rest_area'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-blue-900/20 hover:bg-blue-900/30 py-2 px-1 rounded-xl border border-blue-500/20 hover:border-blue-500/40 flex flex-col items-center transition-all active:scale-95">
                        <Coffee size={12} className="text-blue-500 mb-0.5" />
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.restAreas && (route.stats?.restAreasCount ?? 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.restAreasCount ?? 0)}
                        </span>
                        <span className="text-[7px] font-black text-blue-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.rest_area')}</span>
                        {activeRouteLoading?.restAreas && <Loader2 size={8} className="animate-spin text-blue-400/80 mt-0.5" />}
                     </button>
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'campsites', t('route.camping'))} onTouchStart={handleTouchStart('campsites', t('route.camping'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-indigo-900/20 hover:bg-indigo-900/30 py-2 px-1 rounded-xl border border-indigo-500/20 hover:border-indigo-500/40 flex flex-col items-center transition-all active:scale-95">
                        <Mountain size={12} className="text-indigo-500 mb-0.5" />
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.campsites && (route.stats?.campsitesCount ?? 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.campsitesCount ?? 0)}
                        </span>
                        <span className="text-[7px] font-black text-indigo-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.camping')}</span>
                        {activeRouteLoading?.campsites && <Loader2 size={8} className="animate-spin text-indigo-400/80 mt-0.5" />}
                     </button>

                     {/* Attractions Count */}
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'attractions', t('route.attractions'))} onTouchStart={handleTouchStart('attractions', t('route.attractions'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-violet-900/20 hover:bg-violet-900/30 py-2 px-1 rounded-xl border border-violet-500/20 hover:border-violet-500/40 flex flex-col items-center transition-all active:scale-95">
                        <Landmark size={12} className="text-violet-500 mb-0.5" />
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.attractions && (route.stats?.attractionsCount || 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.attractionsCount || 0)}
                        </span>
                        <span className="text-[7px] font-black text-violet-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.attractions')}</span>
                        {activeRouteLoading?.attractions && <Loader2 size={8} className="animate-spin text-violet-400/80 mt-0.5" />}
                     </button>

                     {/* Charger count */}
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'chargers', t('route.charging'))} onTouchStart={handleTouchStart('chargers', t('route.charging'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-yellow-900/20 hover:bg-yellow-900/30 py-2 px-1 rounded-xl border border-yellow-500/20 hover:border-yellow-500/40 flex flex-col items-center transition-all active:scale-95">
                        <div className="text-yellow-500 mb-0.5 font-black text-2xs">⚡</div>
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.chargers && (route.stats?.chargersCount ?? 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.chargersCount ?? 0)}
                        </span>
                        <span className="text-[7px] font-black text-yellow-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.charging')}</span>
                        {activeRouteLoading?.chargers && <Loader2 size={8} className="animate-spin text-yellow-300/80 mt-0.5" />}
                     </button>

                     {/* Wildlife count */}
                     <button onClick={onShowMap} onContextMenu={(e) => handleContextMenu(e, 'wildlife', t('route.wildlife'))} onTouchStart={handleTouchStart('wildlife', t('route.wildlife'))} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchEnd} className="min-w-0 bg-amber-900/20 hover:bg-amber-900/30 py-2 px-1 rounded-xl border border-amber-500/20 hover:border-amber-500/40 flex flex-col items-center transition-all active:scale-95">
                        <Bird size={12} className="text-amber-500 mb-0.5" />
                        <span className={`text-sm font-black text-[var(--text-primary)] leading-none ${isBackgroundAnalyzing ? 'tabular-nums' : ''}`}>
                           {(activeRouteLoading?.wildlife && (route.stats?.wildlifeCount ?? 0) === 0)
                              ? <Loader2 size={12} className="animate-spin mx-auto" />
                              : (route.stats?.wildlifeCount ?? 0)}
                        </span>
                        <span className="text-[7px] font-black text-amber-500 uppercase tracking-tight leading-none truncate max-w-full">{t('route.wildlife', 'Dyreliv')}</span>
                        {activeRouteLoading?.wildlife && <Loader2 size={8} className="animate-spin text-amber-300/80 mt-0.5" />}
                     </button>
                  </div>

                  {/* Context menu for per-layer re-analysis */}
                  {contextMenu && onReanalyzeLayer && user && (
                     <div
                        className="fixed z-[9999] bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-xl shadow-2xl py-1 min-w-[180px]"
                        style={{
                           left: Math.min(contextMenu.x, window.innerWidth - 200),
                           top: Math.min(contextMenu.y, window.innerHeight - 60)
                        }}
                        onClick={(e) => e.stopPropagation()}
                     >
                        <button
                           onClick={() => { onReanalyzeLayer(route, contextMenu.layer); setContextMenu(null); }}
                           disabled={isBackgroundAnalyzing || !!activeRouteLoading?.[contextMenu.layer as keyof typeof activeRouteLoading]}
                           className="w-full text-left px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-surface)] flex items-center gap-2 transition-colors disabled:opacity-40"
                        >
                           <RefreshCcw size={14} className={activeRouteLoading?.[contextMenu.layer as keyof typeof activeRouteLoading] ? 'animate-spin' : ''} />
                           {t('route.reanalyze_layer', { layer: contextMenu.label })}
                        </button>
                     </div>
                  )}
               </div>
            )}

            {/* Elevation Profile */}
            <ElevationProfile profile={route.elevationProfile} />

            {/* 3D Flyover button — desktop/tablet only */}
            {route.elevationProfile && route.elevationProfile.length >= 2 && route.gpxUrl && (
               <div className="hidden md:block">
                  <button
                     onClick={() => { setShowFlyover(true); logFeatureUsage('flyover_3d'); }}
                     className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 rounded-2xl text-emerald-400 hover:text-emerald-300 transition-all active:scale-[0.98]"
                  >
                     <Orbit size={16} />
                     <span className="text-xs font-black uppercase tracking-widest">{t('route.flyover_3d')}</span>
                  </button>
               </div>
            )}

            {/* 3D Flyover modal (lazy-loaded) */}
            {showFlyover && (
               <Suspense fallback={
                  <div className="fixed inset-0 z-[10003] bg-[var(--bg-surface-alt)] flex items-center justify-center">
                     <div className="flex flex-col items-center gap-3">
                        <Loader2 size={32} className="animate-spin text-emerald-500" />
                        <span className="text-xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                           {t('route.flyover_loading')}
                        </span>
                     </div>
                  </div>
               }>
                  <Route3DFlyover
                     route={route}
                     vehicleCategory={user?.activeVehicleCategory}
                     activeVehicleId={user?.activeVehicleId}
                     userId={user?.uid}
                     onClose={() => setShowFlyover(false)}
                  />
               </Suspense>
            )}

            {/* SVV Webcams along route */}
            {visibleWebcams.length > 0 && (
               <div id="route-webcams" className="bg-cyan-500/5 p-5 rounded-3xl border border-cyan-500/20 space-y-3 animate-in slide-in-from-top-2 duration-300">
                  <button
                     onClick={() => setShowWebcams(!showWebcams)}
                     className="w-full flex items-center justify-between"
                  >
                     <div className="flex items-center gap-2 text-cyan-500 font-black text-3xs uppercase tracking-widest">
                        <Camera size={14} /> {t("auto.routes.routedetailview.webkameraer", "Webkameraer")}
                     </div>
                     <span className="text-4xs font-black uppercase text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded-md border border-cyan-400/20">
                        {visibleWebcams.length} kamera{visibleWebcams.length !== 1 ? 'er' : ''}
                     </span>
                  </button>
                  {showWebcams && (
                     <div className="space-y-3 pt-2">
                        {visibleWebcams.map((cam, index) => (
                           <div key={cam.id} className="relative rounded-2xl overflow-hidden border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]">
                              <button
                                 type="button"
                                 onClick={() => setFullscreenWebcam(cam)}
                                 className="block w-full text-left"
                              >
                                 <img
                                    src={cam.image_url}
                                    alt={cam.name}
                                    className="w-full h-36 object-cover"
                                    loading="lazy"
                                    onError={() => setFailedWebcamIds(prev => new Set(prev).add(cam.id))}
                                 />
                              </button>
                              <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 text-cyan-200 text-3xs font-black uppercase tracking-widest border border-white/10">
                                 #{index + 1}
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                                 <p className="text-2xs font-black text-[var(--text-primary)] leading-tight">{cam.name}</p>
                                 <div className="flex items-center gap-2 mt-0.5">
                                    {cam.road_name && <span className="text-4xs text-[var(--text-secondary)]">{cam.road_name}</span>}
                                    <span className="text-4xs text-cyan-300">{(cam._distance / 1000).toFixed(1)} km unna</span>
                                 </div>
                                 <p className="text-[7px] text-[var(--text-secondary)] mt-0.5">{t("auto.routes.routedetailview.kilde_statens_vegvesen", "Kilde: Statens vegvesen")}</p>
                              </div>
                           </div>
                        ))}
                     </div>
                  )}
               </div>
            )}

            {fullscreenWebcam && (
               <div className="fixed inset-0 z-[10020] bg-black/95 flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b border-white/10">
                     <div className="min-w-0 pr-4">
                        <p className="text-2xs font-black uppercase tracking-widest text-cyan-300">Webkamera</p>
                        <p className="text-sm font-black text-white truncate mt-1">{fullscreenWebcam.name}</p>
                     </div>
                     <button
                        type="button"
                        onClick={() => setFullscreenWebcam(null)}
                        className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/15 text-white flex items-center justify-center shrink-0"
                     >
                        <X size={20} />
                     </button>
                  </div>
                  <div className="flex-1 flex items-center justify-center p-3">
                     <img
                        src={fullscreenWebcam.image_url}
                        alt={fullscreenWebcam.name}
                        className="max-w-full max-h-full object-contain rounded-2xl"
                     />
                  </div>
               </div>
            )}

            {/* External Links */}
            {route.externalLinks && route.externalLinks.length > 0 && (
               <div className="space-y-3">
                  <h3 className="text-xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('route.links_and_info')}</h3>
                  {route.externalLinks.map((link, i) => (
                     <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl hover:bg-[var(--bg-input)] transition-colors"
                     >
                        <div className="p-2 bg-[var(--bg-surface)] rounded-xl text-blue-400">
                           {link.type === 'video' ? <div className="text-red-400">▶</div> : link.type === 'image' ? <ImageIcon size={14} /> : <ExternalLink size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                           <p className="text-xs font-bold text-[var(--text-primary)] truncate">{link.title}</p>
                           <p className="text-3xs text-[var(--text-muted)] truncate">{link.url}</p>
                        </div>
                     </a>
                  ))}
               </div>
            )}

            {/* Recommend for publishing — only for owner of private routes */}
            {!isDraftRoute && user && route.createdBy === user.uid && route.status === 'private' && (
               <button
                  onClick={handleRecommend}
                  disabled={isRecommending}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 rounded-2xl text-emerald-400 hover:text-emerald-300 transition-all active:scale-[0.98] disabled:opacity-50"
               >
                  {isRecommending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  <span className="text-xs font-black uppercase tracking-widest">{t('route.recommend')}</span>
               </button>
            )}

            {!isDraftRoute && route.status === 'pending' && user && route.createdBy === user.uid && (
               <div className="flex items-center justify-center gap-2 py-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-amber-500">
                  <Clock size={16} />
                  <span className="text-xs font-black uppercase tracking-widest">{t('route.recommend_pending')}</span>
               </div>
            )}

            {/* Route Reviews */}
            {!isDraftRoute && (
               <ReviewSystem
                  routeId={route.id}
                  user={user}
                  onOpenAuth={onOpenAuth || (() => { })}
               />
            )}
         </div>

         {/* Share Choice Modal */}
         {showShareModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
               <div className="absolute inset-0 bg-[var(--bg-overlay)] backdrop-blur-md" onClick={() => setShowShareModal(false)} />
               <div className="relative w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-modal p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                  <div className="text-center mb-6">
                     <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <Share2 size={24} className="text-emerald-500" />
                     </div>
                     <h2 className="text-xl font-black text-[var(--text-primary)]">{t('route.share_choice.title')}</h2>
                  </div>

                  <div className="space-y-3">
                     <button
                        onClick={async () => performShare(await getShareUrl(`${APP_URL}/share/route/${route.id}`))}
                        className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 rounded-3xl text-left transition-all active:scale-[0.98] group"
                     >
                        <div className="flex items-center justify-between mb-1">
                           <span className="text-sm font-black text-[var(--text-primary)] group-hover:text-emerald-400 transition-colors">
                              {t('route.share_choice.web_view')}
                           </span>
                           <ExternalLink size={14} className="text-[var(--text-muted)]" />
                        </div>
                        <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                           {t('route.share_choice.web_view_desc')}
                        </p>
                     </button>

                     <button
                        onClick={async () => performShare(await getShareUrl(`${APP_URL}/app/route/${route.id}`))}
                        className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 rounded-3xl text-left transition-all active:scale-[0.98] group"
                     >
                        <div className="flex items-center justify-between mb-1">
                           <span className="text-sm font-black text-[var(--text-primary)] group-hover:text-emerald-400 transition-colors">
                              {t('route.share_choice.app_link')}
                           </span>
                           <MapIcon size={14} className="text-[var(--text-muted)]" />
                        </div>
                        <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                           {t('route.share_choice.app_link_desc')}
                        </p>
                     </button>
                  </div>

                  {/* Map Apps Section */}
                  {route.gpxUrl && (
                     <>
                        <div className="flex items-center gap-2 mt-2">
                           <div className="flex-1 h-px bg-[var(--bg-surface)]" />
                           <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('route.share_choice.map_apps_title')}</span>
                           <div className="flex-1 h-px bg-[var(--bg-surface)]" />
                        </div>

                        {isPaidUser ? (
                           <>
                              {/* POI Category Checkboxes */}
                              <div className="flex gap-2 mt-2 mb-1">
                                 <label
                                    className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-2xl border cursor-pointer transition-all ${includeFricamp ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[var(--bg-surface)] border-[var(--border-secondary)] text-[var(--text-muted)]'}`}
                                    onClick={() => setIncludeFricamp(!includeFricamp)}
                                 >
                                    <Tent size={12} />
                                    <span className="text-2xs font-black uppercase tracking-widest">Fricamp</span>
                                    {route.stats?.wildSpotsCount ? <span className="text-3xs ml-auto opacity-60">{route.stats.wildSpotsCount}</span> : null}
                                 </label>
                                 <label
                                    className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-2xl border cursor-pointer transition-all ${includeAttractions ? 'bg-violet-500/10 border-violet-500/30 text-violet-400' : 'bg-[var(--bg-surface)] border-[var(--border-secondary)] text-[var(--text-muted)]'}`}
                                    onClick={() => setIncludeAttractions(!includeAttractions)}
                                 >
                                    <Landmark size={12} />
                                    <span className="text-2xs font-black uppercase tracking-widest">{t('route.attractions')}</span>
                                    {route.wikiSpots?.length ? <span className="text-3xs ml-auto opacity-60">{route.wikiSpots.length}</span> : null}
                                 </label>
                              </div>
                              <p className="text-3xs text-[var(--text-muted)] text-center mb-1">{t('route.share_choice.poi_hint')}</p>

                              <div className="space-y-3">
                                 <button
                                    onClick={handleOpenGoogleMaps}
                                    disabled={isLoadingGpx}
                                    className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 rounded-3xl text-left transition-all active:scale-[0.98] group disabled:opacity-50"
                                 >
                                    <div className="flex items-center justify-between mb-1">
                                       <span className="text-sm font-black text-[var(--text-primary)] group-hover:text-emerald-400 transition-colors">
                                          {t('route.share_choice.google_maps')}
                                       </span>
                                       {isLoadingGpx ? <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" /> : <Navigation size={14} className="text-[var(--text-muted)]" />}
                                    </div>
                                    <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                                       {t('route.share_choice.google_maps_desc')}
                                    </p>
                                 </button>

                                 <button
                                    onClick={handleShareGpx}
                                    disabled={isLoadingGpx}
                                    className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 rounded-3xl text-left transition-all active:scale-[0.98] group disabled:opacity-50"
                                 >
                                    <div className="flex items-center justify-between mb-1">
                                       <span className="text-sm font-black text-[var(--text-primary)] group-hover:text-emerald-400 transition-colors">
                                          {t('route.share_choice.share_gpx')}
                                       </span>
                                       {isLoadingGpx ? <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" /> : <Share2 size={14} className="text-[var(--text-muted)]" />}
                                    </div>
                                    <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                                       {t('route.share_choice.share_gpx_desc')}
                                    </p>
                                 </button>

                                 <button
                                    onClick={handleDownloadGpx}
                                    disabled={isLoadingGpx}
                                    className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 rounded-3xl text-left transition-all active:scale-[0.98] group disabled:opacity-50"
                                 >
                                    <div className="flex items-center justify-between mb-1">
                                       <span className="text-sm font-black text-[var(--text-primary)] group-hover:text-emerald-400 transition-colors">
                                          {t('route.share_choice.download_gpx')}
                                       </span>
                                       {isLoadingGpx ? <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" /> : <Download size={14} className="text-[var(--text-muted)]" />}
                                    </div>
                                    <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                                       {t('route.share_choice.download_gpx_desc')}
                                    </p>
                                 </button>
                              </div>
                           </>
                        ) : (
                           <button
                              onClick={() => { setShowShareModal(false); showNudge('routes_gpx'); }}
                              className="w-full mt-2 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20 p-4 rounded-3xl text-left transition-all active:scale-[0.98] group"
                           >
                              <div className="flex items-center gap-3">
                                 <div className="p-2 bg-amber-500/10 rounded-xl">
                                    <Lock size={16} className="text-amber-500" />
                                 </div>
                                 <div>
                                    <span className="text-sm font-black text-amber-400">{t('route.share_choice.premium_required')}</span>
                                    <p className="text-tiny text-[var(--text-secondary)] font-medium leading-relaxed">
                                       {t('route.share_choice.premium_required_desc')}
                                    </p>
                                 </div>
                              </div>
                           </button>
                        )}
                     </>
                  )}

                  <button
                     onClick={() => setShowShareModal(false)}
                     className="w-full mt-6 py-4 rounded-3xl text-xs font-black uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  >
                     {t('common.cancel')}
                  </button>
               </div>
            </div>
         )}
         <SubscriptionModal 
            isOpen={showSubscriptionModal} 
            onClose={() => setShowSubscriptionModal(false)} 
         />
      </div>
   );
};

export default RouteDetailView;
